import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      // Pydantic email-validator emits verbose developer-facing messages on 422 —
      // replace with a friendlier line.
      if (status === 422) {
        setError("Please enter a valid email address.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-background" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:col-span-2 flex-col justify-between p-10 border-r border-border bg-card">
        <img src="/brand/elevante-logo.png" alt="elevante.ai" className="h-9 w-auto self-start select-none" draggable={false} />
        <div className="max-w-md">
          <div className="eyebrow mb-3">Account recovery</div>
          <h1 className="text-4xl leading-[1.05] tracking-tighter font-medium">
            Forgot your<br />password?<br /><span className="text-primary">Let&rsquo;s reset it.</span>
          </h1>
          <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-sm">
            Enter your account email and we&rsquo;ll send you a secure link. The link expires in 15 minutes and can only be used once.
          </p>
        </div>
        <div />
      </div>

      <div className="lg:col-span-3 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm space-y-6">
          {submitted ? (
            <div className="space-y-4" data-testid="forgot-submitted">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <div>
                <div className="eyebrow mb-2">Check your inbox</div>
                <h2 className="text-2xl tracking-tight font-medium">If that email exists, we sent a link.</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  The link expires in 15 minutes. Didn&rsquo;t get anything? Check spam, or wait a minute and try again — we deliberately don&rsquo;t confirm whether an email is registered.
                </p>
              </div>
              <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid="back-to-login">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <div className="eyebrow mb-2">Reset password</div>
                <h2 className="text-2xl tracking-tight font-medium">Send me a reset link</h2>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp-email">Account email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && (
                <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="forgot-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading || !email}
                data-testid="forgot-submit"
                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-to-login-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
